import type { SubmissionView, GameItem, PlayerStanding } from "@/types/api";

// --- Photo selection ---

interface SelectedPhoto {
  submission: SubmissionView;
  itemName: string;
  playerName: string;
}

export function selectPhotos(
  submissions: SubmissionView[],
  items: GameItem[],
  playerNames: Record<string, string>,
  limit = 9,
): SelectedPhoto[] {
  const itemMap = new Map(items.map((i) => [i.itemId, i.name]));
  const approved = submissions
    .filter((s) => s.moderationStatus === "approved")
    .sort((a, b) => b.voteCount - a.voteCount);

  const selected: SelectedPhoto[] = [];
  const usedPlayers = new Set<string>();

  // First pass: prefer unique players for variety
  for (const sub of approved) {
    if (selected.length >= limit) break;
    if (usedPlayers.has(sub.playerId)) continue;
    usedPlayers.add(sub.playerId);
    selected.push({
      submission: sub,
      itemName: itemMap.get(sub.itemId) ?? "Item",
      playerName: playerNames[sub.playerId] ?? "Player",
    });
  }

  // Second pass: fill remaining slots with top-voted photos
  if (selected.length < limit) {
    const selectedIds = new Set(selected.map((s) => s.submission.submissionId));
    for (const sub of approved) {
      if (selected.length >= limit) break;
      if (selectedIds.has(sub.submissionId)) continue;
      selected.push({
        submission: sub,
        itemName: itemMap.get(sub.itemId) ?? "Item",
        playerName: playerNames[sub.playerId] ?? "Player",
      });
    }
  }

  return selected;
}

// --- Image loading ---

export async function loadImages(
  urls: string[],
): Promise<(ImageBitmap | null)[]> {
  const results = await Promise.allSettled(
    urls.map(async (url) => {
      // cache: "no-store" prevents the browser from serving a cached
      // non-CORS response that <img> tags stored earlier, which would
      // cause fetch() to fail on the second call.
      const res = await fetch(url, { cache: "no-store" });
      const blob = await res.blob();
      return createImageBitmap(blob);
    }),
  );
  return results.map((r) => (r.status === "fulfilled" ? r.value : null));
}

// --- Grid layout ---

interface GridLayout {
  cols: number;
  rows: number;
}

function getGridLayout(count: number): GridLayout {
  if (count <= 1) return { cols: 1, rows: 1 };
  if (count === 2) return { cols: 2, rows: 1 };
  if (count === 3) return { cols: 3, rows: 1 };
  if (count === 4) return { cols: 2, rows: 2 };
  if (count <= 6) return { cols: 3, rows: 2 };
  return { cols: 3, rows: 3 };
}

// --- Canvas drawing helpers ---

function roundedRect(
  ctx: OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawImageCover(
  ctx: OffscreenCanvasRenderingContext2D,
  img: ImageBitmap,
  x: number,
  y: number,
  w: number,
  h: number,
  radius: number,
) {
  ctx.save();
  roundedRect(ctx, x, y, w, h, radius);
  ctx.clip();

  // Cover: scale to fill, center crop
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

// --- Main collage generation ---

export interface CollageInput {
  gameTitle: string;
  standings: PlayerStanding[];
  photos: SelectedPhoto[];
  images: (ImageBitmap | null)[];
}

export async function generateCollage(input: CollageInput): Promise<Blob> {
  const { gameTitle, standings, photos, images } = input;
  const W = 1080;
  const H = 1920;
  const canvas = new OffscreenCanvas(W, H);
  const ctx = canvas.getContext("2d")!;

  // Wait for fonts
  await document.fonts.ready;

  // Background gradient
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, "#6366f1");
  grad.addColorStop(1, "#1e1b4b");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // --- Header (0–140) ---
  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 56px Nunito, sans-serif";
  ctx.fillText("ISPYFR", W / 2, 70);
  ctx.font = "bold 28px Nunito, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.fillText("GAME RECAP", W / 2, 115);

  // --- Game title (140–260) ---
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 40px Nunito, sans-serif";
  // Truncate long titles
  let title = gameTitle;
  while (ctx.measureText(title).width > W - 80 && title.length > 3) {
    title = title.slice(0, -1);
  }
  if (title !== gameTitle) title += "...";
  ctx.fillText(title, W / 2, 210);

  // --- Photo grid (260–~1300) ---
  const validPhotos = photos.filter((_, i) => images[i] !== null);
  const validImages = images.filter((img): img is ImageBitmap => img !== null);
  const photoCount = validImages.length;

  let gridBottom = 260;

  if (photoCount > 0) {
    const { cols, rows } = getGridLayout(photoCount);
    const gridPadding = 40;
    const gap = 12;
    const gridW = W - gridPadding * 2;
    const cellW = (gridW - (cols - 1) * gap) / cols;
    const cellH = cellW; // Square cells
    const labelH = 28; // Space for item name below photo
    const totalGridH = rows * (cellH + labelH) + (rows - 1) * gap;
    const gridTop = 270;
    gridBottom = gridTop + totalGridH + 20;

    for (let i = 0; i < Math.min(photoCount, cols * rows); i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gridPadding + col * (cellW + gap);
      const y = gridTop + row * (cellH + labelH + gap);

      // White border (draw slightly larger rect behind)
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      roundedRect(ctx, x - 3, y - 3, cellW + 6, cellH + 6, 14);
      ctx.fill();

      // Photo
      drawImageCover(ctx, validImages[i]!, x, y, cellW, cellH, 12);

      // Item name below photo
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.font = "600 18px Nunito, sans-serif";
      ctx.textAlign = "center";
      let name = validPhotos[i]?.itemName ?? "";
      if (ctx.measureText(name).width > cellW) {
        while (ctx.measureText(name + "...").width > cellW && name.length > 2) {
          name = name.slice(0, -1);
        }
        name += "...";
      }
      ctx.fillText(name, x + cellW / 2, y + cellH + 22);
    }
  }

  // --- Winner / Scores section ---
  const scoresTop = Math.max(gridBottom + 20, photoCount === 0 ? 300 : gridBottom + 20);
  const sorted = [...standings].sort((a, b) => b.totalScore - a.totalScore);

  if (sorted.length > 0) {
    // Trophy + WINNER
    ctx.textAlign = "center";
    ctx.font = "bold 32px Nunito, sans-serif";
    ctx.fillStyle = "#FBBF24";
    ctx.fillText("\uD83C\uDFC6  WINNER", W / 2, scoresTop + 40);

    // Winner name + score
    const winner = sorted[0]!;
    ctx.font = "bold 48px Nunito, sans-serif";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(
      `${winner.displayName} \u2014 ${winner.approvedCount}/${winner.totalItems}`,
      W / 2,
      scoresTop + 100,
    );

    // Score detail
    if (winner.voteBonus > 0) {
      ctx.font = "600 22px Nunito, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.fillText(
        `${winner.approvedCount} items + ${winner.voteBonus} vote bonus = ${winner.totalScore} pts`,
        W / 2,
        scoresTop + 135,
      );
    }

    // 2nd and 3rd
    if (sorted.length >= 2) {
      ctx.font = "bold 28px Nunito, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      let runnerUp = `2nd: ${sorted[1]!.displayName} (${sorted[1]!.totalScore})`;
      if (sorted.length >= 3) {
        runnerUp += `   3rd: ${sorted[2]!.displayName} (${sorted[2]!.totalScore})`;
      }
      ctx.fillText(runnerUp, W / 2, scoresTop + 180);
    }

    // All other players (compact)
    if (sorted.length > 3) {
      ctx.font = "600 20px Nunito, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.5)";
      const rest = sorted
        .slice(3)
        .map((p) => `${p.displayName}: ${p.totalScore}`)
        .join("  \u00B7  ");
      ctx.fillText(rest, W / 2, scoresTop + 220);
    }
  }

  // --- Watermark (bottom 120px) ---
  ctx.textAlign = "center";
  ctx.font = "bold 28px Nunito, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.5)";
  ctx.fillText("Made with ISPYFR", W / 2, H - 70);
  ctx.font = "600 22px Nunito, sans-serif";
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.fillText("ispyfr.com", W / 2, H - 35);

  // Export
  const blob = await canvas.convertToBlob({ type: "image/jpeg", quality: 0.85 });

  // Cleanup bitmaps
  for (const img of images) {
    if (img) img.close();
  }

  return blob;
}
